import React, { useCallback, useEffect } from 'react'
import { Button, Modal } from 'react-bootstrap';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { UpdateRolesFormData, UpdateRolesSchema } from '../schema/UpdateRolesSchema';
import { useRoleById } from '../hooks/useRoleById';
import { useUpdateRole } from '../hooks/useUpdateRole';

interface UpdateRoleDialogProps {
    show: boolean;
    onClose: () => void;
    roleId: number | undefined;
}

const validationRoleId = z.object({
    roleId: z.number().int().positive(),
});

const UpdateRoleDialog: React.FC<UpdateRoleDialogProps> = ({
    show,
    onClose,
    roleId
}) => {
    const { data: permission, isFetching, isError, isSuccess, error } = useRoleById({
        id: roleId
    });

    if (isError) {
        toast.error(error?.message || 'Failed to fetch role', {
            id: 'fetch-role',
        });
        onClose();
        return;
    }

    const { register, handleSubmit, formState: { errors }, reset, setError } = useForm<UpdateRolesFormData>({
        resolver: zodResolver(UpdateRolesSchema),
    })

    useEffect(() => {
        if (permission) {
            reset({
                name: permission.data.display_name ?? '',
            });
        }
    }, [permission, reset]);

    const handleClose = () => {
        reset();
        onClose();
    }

    const queryClient = useQueryClient();

    const { mutate, isPending, isError: isUpdateError, error: updateError } = useUpdateRole();

    if (isUpdateError) {
        toast.error(updateError?.message || 'invalid role id', {
            id: 'fetch-role',
        });

        handleClose();

        return;
    }

    const onSubmit = useCallback((values: UpdateRolesFormData) => {
        toast.loading('Updating role...', {
            id: 'update-role',
        });

        const validationIdResult = validationRoleId.safeParse({ roleId });

        if (!validationIdResult.success) {
            toast.error('Invalid role id', {
                id: 'update-role',
            });
            return;
        }

        mutate({
            roleId: validationIdResult.data.roleId,
            formData: values,
        }, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['roles'].includes(key as string)),
                });

                toast.success('Role updated successfully', {
                    id: 'update-role',
                });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof UpdateRolesFormData, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to update role', { id: 'update-role' });
                    }
                } else {
                    toast.error((mutationError as any)?.response?.data?.message || 'Failed to update permission', { id: 'update-permission' });
                }
            }
        });
    }, [mutate, roleId]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Update Permission</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isFetching && <FormSpinner />}
                {isSuccess && (
                    <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column">
                        <div className="mb-3">
                            <label htmlFor="name" className="form-label">Name</label>
                            <input
                                type="text"
                                className={`form-control ${errors.name && 'is-invalid'}`}
                                id="name"
                                placeholder='Enter permission name'
                                {...register('name')}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                        </div>
                    </form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                <Button variant="primary" type='submit' onClick={handleSubmit(onSubmit)} disabled={isPending}>
                    {isPending ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal >
    )
}

export default UpdateRoleDialog